import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	redirect,
	useRouteContext,
} from "@tanstack/react-router";
import {
	CalendarDays,
	ClipboardList,
	GraduationCap,
	Loader2,
	TrendingUp,
} from "lucide-react";
import { type LeadStatus, STATUS_CATEGORIES } from "#/lib/lead-status";
import { canViewLeads, homeRouteFor } from "#/lib/roles";
import { getSession } from "#/lib/session";

export const Route = createFileRoute("/admin/")({
	beforeLoad: async () => {
		const session = await getSession();
		if (!session?.user) {
			throw redirect({ to: "/admin/login" });
		}
		if (!canViewLeads(session.user.role)) {
			throw redirect({ to: homeRouteFor(session.user.role) });
		}
	},
	component: DashboardPage,
});

function formatDate(value: string | Date) {
	return new Intl.DateTimeFormat("ar-DZ", {
		dateStyle: "medium",
		timeStyle: "short",
		timeZone: "Africa/Algiers",
	}).format(new Date(value));
}

function DashboardPage() {
	const { trpc } = useRouteContext({ from: "__root__" });
	const statsQuery = useQuery(trpc.dashboard.stats.queryOptions());
	const stats = statsQuery.data;

	return (
		<div className="flex flex-col gap-6">
			<header>
				<h1 className="text-2xl font-bold text-navy">اللوحة الرئيسية</h1>
				<p className="text-sm text-muted">نظرة عامة على الطلبات والتدريبات.</p>
			</header>

			{statsQuery.isLoading || !stats ? (
				<div className="flex justify-center py-20">
					<Loader2 className="h-6 w-6 animate-spin text-muted" />
				</div>
			) : (
				<>
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
						<StatCard
							icon={ClipboardList}
							label="إجمالي الطلبات"
							value={stats.totalLeads}
						/>
						<StatCard
							icon={CalendarDays}
							label="طلبات اليوم"
							value={stats.newToday}
						/>
						<StatCard
							icon={TrendingUp}
							label="طلبات آخر 7 أيام"
							value={stats.newThisWeek}
						/>
						<StatCard
							icon={GraduationCap}
							label="التدريبات المفعّلة"
							value={`${stats.activeTrainings} / ${stats.totalTrainings}`}
						/>
					</div>

					<div className="grid gap-4 lg:grid-cols-2">
						<StatusBreakdown
							byStatus={stats.byStatus}
							total={stats.totalLeads}
						/>
						<TrainingBreakdown byTraining={stats.byTraining} />
					</div>

					<div className="rounded-2xl border border-line bg-white shadow-card">
						<div className="border-b border-line px-5 py-4">
							<h2 className="text-sm font-bold text-navy">أحدث الطلبات</h2>
						</div>
						<div className="overflow-x-auto">
							<table className="w-full min-w-[640px] text-start text-sm">
								<thead>
									<tr className="border-b border-line bg-canvas text-muted">
										<th className="px-4 py-2.5 text-start font-bold">الاسم</th>
										<th className="px-4 py-2.5 text-start font-bold">
											التدريب
										</th>
										<th className="px-4 py-2.5 text-start font-bold">
											التاريخ
										</th>
									</tr>
								</thead>
								<tbody>
									{stats.recentLeads.map((lead) => (
										<tr
											key={lead.id}
											className="border-b border-line last:border-0 hover:bg-canvas/60"
										>
											<td className="px-4 py-2.5 font-semibold text-ink">
												{lead.fullName}
											</td>
											<td className="px-4 py-2.5">
												<Link
													to="/admin/leads"
													search={{ trainingId: lead.training.id }}
													className="text-teal-600 hover:underline"
												>
													{lead.training.name}
												</Link>
											</td>
											<td className="whitespace-nowrap px-4 py-2.5 text-muted">
												{formatDate(lead.createdAt)}
											</td>
										</tr>
									))}
									{stats.recentLeads.length === 0 ? (
										<tr>
											<td
												colSpan={3}
												className="px-4 py-10 text-center text-muted"
											>
												لا توجد طلبات بعد.
											</td>
										</tr>
									) : null}
								</tbody>
							</table>
						</div>
					</div>
				</>
			)}
		</div>
	);
}

function StatCard({
	icon: Icon,
	label,
	value,
}: {
	icon: typeof ClipboardList;
	label: string;
	value: string | number;
}) {
	return (
		<div className="flex items-center gap-3 rounded-2xl border border-line bg-white p-4 shadow-card">
			<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
				<Icon className="h-5 w-5" />
			</div>
			<div>
				<p className="text-xl font-bold text-navy">
					{typeof value === "number" ? value.toLocaleString("ar-DZ") : value}
				</p>
				<p className="text-xs font-semibold text-muted">{label}</p>
			</div>
		</div>
	);
}

function StatusBreakdown({
	byStatus,
	total,
}: {
	byStatus: Record<LeadStatus, number>;
	total: number;
}) {
	return (
		<div className="rounded-2xl border border-line bg-white p-5 shadow-card">
			<h2 className="mb-4 text-sm font-bold text-navy">توزيع الحالات</h2>
			<div className="flex flex-col gap-3">
				{STATUS_CATEGORIES.map((cat) => {
					const count = cat.statuses.reduce(
						(sum, s) => sum + (byStatus[s] ?? 0),
						0,
					);
					const pct = total > 0 ? Math.round((count / total) * 100) : 0;
					return (
						<div key={cat.key} className="flex flex-col gap-1">
							<div className="flex items-center justify-between text-xs font-semibold">
								<span className="text-navy">{cat.label}</span>
								<span className="text-muted">
									{count.toLocaleString("ar-DZ")} ({pct}%)
								</span>
							</div>
							<div className="h-2 w-full overflow-hidden rounded-full bg-canvas">
								<div
									className="h-full rounded-full bg-teal"
									style={{ width: `${pct}%` }}
								/>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}

function TrainingBreakdown({
	byTraining,
}: {
	byTraining: Array<{
		id: string;
		name: string;
		slug: string;
		isActive: boolean;
		isDefault: boolean;
		count: number;
	}>;
}) {
	const max = Math.max(1, ...byTraining.map((t) => t.count));
	return (
		<div className="rounded-2xl border border-line bg-white p-5 shadow-card">
			<h2 className="mb-4 text-sm font-bold text-navy">الطلبات حسب التدريب</h2>
			<div className="flex flex-col gap-3">
				{byTraining.map((training) => (
					<Link
						key={training.id}
						to="/admin/leads"
						search={{ trainingId: training.id }}
						className="flex flex-col gap-1 rounded-lg p-1.5 -m-1.5 transition-colors hover:bg-canvas"
					>
						<div className="flex items-center justify-between text-xs font-semibold">
							<span className="text-navy">
								{training.name}
								{!training.isActive ? (
									<span className="ms-1.5 rounded-md bg-line/50 px-1.5 py-0.5 text-[0.65rem] text-muted">
										معطّل
									</span>
								) : null}
							</span>
							<span className="text-muted">
								{training.count.toLocaleString("ar-DZ")}
							</span>
						</div>
						<div className="h-2 w-full overflow-hidden rounded-full bg-canvas">
							<div
								className="h-full rounded-full bg-navy"
								style={{ width: `${(training.count / max) * 100}%` }}
							/>
						</div>
					</Link>
				))}
				{byTraining.length === 0 ? (
					<p className="text-sm text-muted">لا توجد تدريبات بعد.</p>
				) : null}
			</div>
		</div>
	);
}
